import {
    Box,
    Button,
    Card,
    CardContent,
    CardActions,
    // Collapse,
    Menu,
    MenuItem,
    Divider,
    IconButton,
    Typography,
    styled,
    Tooltip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import LoadingWrapper from 'src/components/Loading/LoadingWrapper';
import MessagesService from 'src/services/MessagesService';
import useMessagesStore from 'src/stores/messages';
import { useClipboard } from 'src/hooks/useClipboard';
import ArrowUpRightInCircle from 'src/icons/ArrowUpRightInCircle';
import Bookmark from 'src/icons/Bookmark';
import Checked from 'src/icons/Checked';
import Copy from 'src/icons/Copy';
import Geopoint from 'src/icons/Geopoint';
import TelegramLogo from 'src/icons/TelegramLogo';
import TrashIcon from 'src/icons/TrashIcon';

// import CollapseIcon from 'src/icons/CollapseIcon';

import { getTgNickNameByLink } from '../utils';
import useLeadMutationPending from '../useLeadMutationPending';

import { getFeedbackLabel, getSavedFeedback, writeFeedback } from './leadFeedback';

import type { IMessage } from 'src/types/Message';
import type { LeadFeedback } from './leadFeedback';

type Props = {
    body: IMessage;
    onOpen?: (id: number, element: HTMLElement) => void;
    isSelected?: boolean;
    details?: boolean;
    checkbox?: React.ReactNode;
    onDeletingStart?: (messageId: number) => void;
    onDeletingEnd?: (messageId: number) => void;
};

const FeedbackActionButton = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== 'feedbackTone' && prop !== 'isActive',
})<{ feedbackTone: 'good' | 'bad'; isActive: boolean }>(({ feedbackTone, isActive }) => {
    const isGood = feedbackTone === 'good';
    const activeColor = isGood ? 'var(--lm-success-ink)' : 'var(--lm-danger-ink)';
    const activeBackground = isGood ? 'var(--lm-success-bg)' : 'var(--lm-danger-bg)';
    const activeBorder = isGood ? 'var(--lm-success-border)' : 'var(--lm-danger-border)';

    return {
        width: 34,
        height: 34,
        border: `1px solid ${isActive ? activeBorder : 'var(--lm-border)'}`,
        borderRadius: '8px',
        color: isActive ? activeColor : 'var(--lm-muted)',
        backgroundColor: isActive ? activeBackground : 'var(--lm-surface)',
        padding: 0,
        '&:hover': {
            borderColor: isActive ? activeBorder : isGood ? 'var(--lm-success-border)' : 'var(--lm-danger-border)',
            backgroundColor: isActive ? activeBackground : isGood ? 'var(--lm-success-bg)' : 'var(--lm-danger-bg)',
        },
    };
});

const BadFeedbackIcon = styled('span')(() => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 20,
    fontWeight: 400,
    lineHeight: '14px',
    width: 18,
}));

const getCardFeedbackSx = (feedback: LeadFeedback) => {
    const baseSx = {
        transition: 'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease, opacity 160ms ease',
    };

    if (feedback === 'good') {
        return {
            ...baseSx,
            backgroundColor: 'var(--lm-success-bg)',
            borderColor: 'var(--lm-success-border)',
            boxShadow: '0 6px 18px rgba(22, 121, 76, 0.08)',
        };
    }

    if (feedback === 'bad') {
        return {
            ...baseSx,
            backgroundColor: 'var(--lm-canvas)',
            borderColor: 'var(--lm-border)',
            boxShadow: 'none',
            opacity: 0.72,
        };
    }

    return baseSx;
};

const getInitialFeedback = (message: IMessage): LeadFeedback => {
    return typeof message.feedback === 'undefined' ? getSavedFeedback(message.id) : message.feedback;
};

export default ({
    body,
    checkbox,
    onDeletingStart,
    onDeletingEnd,
    onOpen,
    isSelected = false,
    details = false,
}: Props) => {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isRemoved, setIsRemoved] = useState(false); // Локальное состояние для немедленного скрытия
    const [feedback, setFeedback] = useState<LeadFeedback>(() => getInitialFeedback(body));
    const { copy } = useClipboard();
    const open = Boolean(anchorEl);
    const isFavoriteOnly = false; // Убираем проверку на отдельную страницу favorites
    const feedbackLabel = getFeedbackLabel(feedback);
    const messageAge = (() => {
        const ago = dayjs(body.date).fromNow(true);
        if (ago === 'час') return '1 час';
        return ago;
    })();
    const messageTime = dayjs(body.date).format('HH:mm');

    const handleClose = () => {
        setAnchorEl(null);
    };

    const isLoading = useLeadMutationPending('lead-favorite', body.id);
    const isFeedbackSaving = useLeadMutationPending('lead-feedback', body.id);
    const { mutateAsync } = useMutation(useMessagesStore.getState().toggleFavorite, {
        mutationKey: ['lead-favorite', body.id],
        onSuccess: (_, variables) => {
            const isAdding = variables.flag;
            enqueueSnackbar(isAdding ? 'Сообщение добавлено в избранное' : 'Сообщение удалено из избранного', {
                variant: 'success',
            });
            handleClose(); // Закрываем меню после успешного изменения
            // Инвалидируем кеш запросов сообщений для обновления списка
            queryClient.invalidateQueries(['fetch-message']);
        },
        onError: () => {
            enqueueSnackbar('Произошла ошибка. Попробуйте повторить позже', { variant: 'error' });
        },
    });

    const { isLoading: isLoadingRemove, mutateAsync: removeMessage } = useMutation(
        useMessagesStore.getState().removeMessage,
        {
            onMutate: async (messageId) => {
                setIsRemoved(true);
                onDeletingStart?.(messageId);

                await queryClient.cancelQueries({ queryKey: ['fetch-message'] });

                const previousQueries = queryClient.getQueriesData({ queryKey: ['fetch-message'] });
                const previousStoreState = useMessagesStore.getState();

                // Оптимистично обновляем кеш React Query - удаляем сообщение из всех страниц
                previousQueries.forEach(([queryKey, oldData]) => {
                    if (!oldData || !(oldData as any).pages) return;

                    queryClient.setQueryData(queryKey, (old: any) => {
                        if (!old || !old.pages) return old;

                        const updatedPages = old.pages.map((page: any) => ({
                            ...page,
                            results: (page.results || []).filter((msg: IMessage) => msg.id !== messageId),
                        }));

                        return {
                            ...old,
                            pages: updatedPages,
                            // Обновляем count в первой странице
                            ...(updatedPages[0] && {
                                pages: updatedPages.map((page: any, index: number) =>
                                    index === 0 ? { ...page, count: Math.max(0, (page.count || 0) - 1) } : page
                                ),
                            }),
                        };
                    });
                });

                // Оптимистично обновляем store
                const updatedMessages = previousStoreState.messages.filter((msg) => msg.id !== messageId);
                const newCount =
                    previousStoreState.count > 0 ? Math.max(0, previousStoreState.count - 1) : previousStoreState.count;

                useMessagesStore.setState({
                    messages: updatedMessages,
                    count: newCount,
                });

                return { previousQueries, previousStoreState, messageId };
            },
            onSuccess: (_, messageId) => {
                enqueueSnackbar('Сообщение успешно удалено', { variant: 'success' });
                handleClose();
                onDeletingEnd?.(messageId);
                // НЕ инвалидируем кеш - оптимистичное обновление уже сделано
                // Инвалидация может вызвать повторную загрузку и дубли
            },
            onError: (_error, messageId, context) => {
                setIsRemoved(false);
                onDeletingEnd?.(messageId);

                if (context?.previousStoreState) {
                    useMessagesStore.setState({
                        messages: context.previousStoreState.messages,
                        count: context.previousStoreState.count,
                    });
                }

                if (context?.previousQueries) {
                    context.previousQueries.forEach(([queryKey, data]) => {
                        queryClient.setQueryData(queryKey, data);
                    });
                }

                enqueueSnackbar('Неудалось удалить сообщение. Попробуйте повторить позже', { variant: 'error' });
            },
        }
    );

    const syncFeedbackInQueryCache = (messageId: number, nextFeedback: LeadFeedback) => {
        const previousQueries = queryClient.getQueriesData({ queryKey: ['fetch-message'] });

        previousQueries.forEach(([queryKey]) => {
            queryClient.setQueryData(queryKey, (oldData: any) => {
                if (!oldData) return oldData;

                if (oldData.pages) {
                    return {
                        ...oldData,
                        pages: oldData.pages.map((page: any) => ({
                            ...page,
                            results: (page.results || []).map((message: IMessage) =>
                                message.id === messageId ? { ...message, feedback: nextFeedback } : message
                            ),
                        })),
                    };
                }

                if (oldData.results) {
                    return {
                        ...oldData,
                        results: oldData.results.map((message: IMessage) =>
                            message.id === messageId ? { ...message, feedback: nextFeedback } : message
                        ),
                    };
                }

                return oldData;
            });
        });

        return previousQueries;
    };

    const { mutateAsync: saveFeedback } = useMutation(
        ({ id, nextFeedback }: { id: number; nextFeedback: LeadFeedback }) =>
            MessagesService.saveMessageFeedback(id, nextFeedback),
        {
            mutationKey: ['lead-feedback', body.id],
            onMutate: async ({ id, nextFeedback }) => {
                await queryClient.cancelQueries({ queryKey: ['fetch-message'] });

                const previousFeedback = feedback;
                const previousStoreState = useMessagesStore.getState();
                const previousQueries = syncFeedbackInQueryCache(id, nextFeedback);

                setFeedback(nextFeedback);
                writeFeedback(id, nextFeedback);
                useMessagesStore.getState().setFeedback(id, nextFeedback);

                return { previousFeedback, previousQueries, previousStoreState };
            },
            onSuccess: (savedFeedback, variables) => {
                setFeedback(savedFeedback);
                writeFeedback(variables.id, savedFeedback);
                syncFeedbackInQueryCache(variables.id, savedFeedback);
                useMessagesStore.getState().setFeedback(variables.id, savedFeedback);

                enqueueSnackbar(
                    savedFeedback ? getFeedbackLabel(savedFeedback) || 'Оценка сохранена' : 'Оценка снята',
                    {
                        variant: savedFeedback === 'bad' ? 'info' : 'success',
                    }
                );
            },
            onError: (_error, variables, context) => {
                const previousFeedback = context?.previousFeedback ?? null;

                setFeedback(previousFeedback);
                writeFeedback(variables.id, previousFeedback);

                if (context?.previousStoreState) {
                    useMessagesStore.setState({
                        messages: context.previousStoreState.messages,
                        count: context.previousStoreState.count,
                    });
                }

                if (context?.previousQueries) {
                    context.previousQueries.forEach(([queryKey, data]) => {
                        queryClient.setQueryData(queryKey, data);
                    });
                }

                enqueueSnackbar('Не удалось сохранить оценку лида. Попробуйте повторить позже', { variant: 'error' });
            },
        }
    );

    const setFavoriteStatus = async () => {
        try {
            await mutateAsync({ id: body.id, flag: !body.favorite });

            // Если сообщение было в избранном и мы находимся в режиме только избранного,
            // удаляем его из списка
            if (!!body.favorite && isFavoriteOnly) {
                useMessagesStore.getState().deleteFromFavorite(body.id);
            }
        } catch (_error) {
            // Ошибка уже обработана в onError
        }
    };

    const handleFavoriteClick = () => {
        if (!isLoading) {
            setFavoriteStatus();
        }
    };

    const handleRemove = () => {
        if (!isLoadingRemove) {
            removeMessage(body.id).catch(() => {
                // Ошибка уже обработана в onError
            });
        }
    };

    const handleFeedback = (value: Exclude<LeadFeedback, null>) => {
        if (isFeedbackSaving) return;

        const nextFeedback = feedback === value ? null : value;

        saveFeedback({
            id: body.id,
            nextFeedback,
        }).catch(() => {
            // Ошибка уже обработана в onError
        });
    };

    useEffect(() => {
        setIsRemoved(false);
        setFeedback(getInitialFeedback(body));
    }, [body.id, body.feedback]);

    const renderContextMenuCard = () => {
        return (
            <Menu
                anchorEl={anchorEl}
                id="card-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                //disable lock for window
                //disableScrollLock
                // marginThreshold={null}
            >
                <Box
                    width={240}
                    sx={{
                        background: 'var(--lm-canvas)',
                    }}
                    mt={-1}
                    p={0.5}
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        width={1}
                        height={54}
                        p={1}
                        borderRadius={3}
                        onClick={handleFavoriteClick}
                        style={{
                            cursor: 'pointer',
                        }}
                        bgcolor="background.default"
                    >
                        <Bookmark fill={body.favorite} />
                        <Typography variant="overline">Добавить в избранное</Typography>
                    </Box>
                </Box>
                <MenuItem
                    onClick={() => {
                        copy(body.link);
                    }}
                >
                    Скопировать ссылку <Copy />
                </MenuItem>
                {/* Удаление доступно только для сообщений из мониторинга */}
                {body.source !== 'stream' && (
                    <>
                        <Divider />
                        <MenuItem
                            disabled={isLoadingRemove}
                            onClick={handleRemove}
                        >
                            Удалить сообщение <TrashIcon />
                        </MenuItem>
                    </>
                )}
            </Menu>
        );
    };
    const renderTitle = () => {
        const isFromStream = body.source === 'stream' && body.stream_name;
        const sourceName = isFromStream ? body.stream_display_name || body.stream_name : body.chat_name;

        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                gap={0.25}
                maxWidth="100%"
                overflow="hidden"
            >
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    maxWidth="100%"
                    overflow="hidden"
                >
                    <Typography
                        variant="body2"
                        noWrap
                        title={sourceName}
                        sx={{ fontWeight: 600 }}
                    >
                        {sourceName}
                    </Typography>
                    {sourceName && (
                        <Typography
                            variant="body2"
                            color="textDisabled"
                            sx={{ fontWeight: 300 }}
                        >
                            от
                        </Typography>
                    )}
                    <Typography
                        variant="body2"
                        noWrap
                        title={body.author}
                        sx={{ fontWeight: 400 }}
                    >
                        {getTgNickNameByLink(body.author)}
                    </Typography>
                    {body.link && (
                        <Tooltip title="Открыть сообщение">
                            <IconButton
                                component="a"
                                href={body.link}
                                target="_blank"
                                rel="noreferrer"
                                size="small"
                                aria-label="Открыть сообщение"
                                sx={{
                                    width: 24,
                                    height: 24,
                                    ml: 0.25,
                                    p: 0.25,
                                    flexShrink: 0,
                                    color: '#4B5D72',
                                    '& svg circle': {
                                        stroke: 'currentColor',
                                    },
                                    '& svg path': {
                                        fill: 'currentColor',
                                    },
                                    '&:hover': {
                                        bgcolor: 'var(--lm-canvas)',
                                        color: 'var(--lm-text)',
                                    },
                                }}
                                onClick={(event) => event.stopPropagation()}
                            >
                                <ArrowUpRightInCircle />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                {body.location && (
                    <Tooltip title="Локация группы">
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            maxWidth="100%"
                            overflow="hidden"
                            sx={{ color: 'text.disabled' }}
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                sx={{ opacity: 0.45 }}
                            >
                                <Geopoint />
                            </Box>
                            <Typography
                                variant="body2"
                                color="textDisabled"
                                noWrap
                                title={body.location}
                                sx={{ fontSize: '12px', lineHeight: 1.3 }}
                            >
                                {body.location}
                            </Typography>
                        </Box>
                    </Tooltip>
                )}
            </Box>
        );
    };

    const renderSource = () => {
        return (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                gap={0.5}
                maxWidth="100%"
                overflow="hidden"
                sx={{ color: 'text.disabled' }}
            >
                <Typography
                    variant="body2"
                    color="textDisabled"
                    noWrap
                    sx={{ fontWeight: 400 }}
                >
                    Источник:
                </Typography>
                <Box
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    minWidth={0}
                    overflow="hidden"
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        flexShrink={0}
                        sx={{ opacity: 0.75 }}
                    >
                        <TelegramLogo
                            width={16}
                            height={16}
                            color="var(--lm-muted)"
                        />
                    </Box>
                    <Typography
                        variant="body2"
                        color="textDisabled"
                        noWrap
                        sx={{ fontWeight: 400 }}
                    >
                        Telegram
                    </Typography>
                </Box>
            </Box>
        );
    };

    const renderMessage = () => {
        // Явно проверяем relevance === true
        const isRelevant = body.relevance === true;
        const shouldHighlightMessage = isRelevant && feedback !== 'bad';

        return (
            <>
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={1}
                >
                    <Box
                        display="block"
                        width={1}
                        sx={{
                            borderRadius: '8px',
                            boxShadow: !details && shouldHighlightMessage ? '0 0 0 1px var(--lm-border)' : 'none',
                        }}
                    >
                        <Box
                            component={onOpen ? 'button' : 'div'}
                            type={onOpen ? 'button' : undefined}
                            data-lead-open={onOpen ? body.id : undefined}
                            aria-label={onOpen ? `Открыть лид: ${body.content.slice(0, 80)}` : undefined}
                            aria-expanded={onOpen ? isSelected : undefined}
                            onClick={onOpen ? (event) => onOpen(body.id, event.currentTarget) : undefined}
                            sx={{
                                width: '100%',
                                textAlign: 'left',
                                color: 'inherit',
                                background: 'transparent',
                                border: 0,
                                cursor: onOpen ? 'pointer' : 'text',
                                p: details ? 0 : '8px 10px',
                                borderRadius: '8px',
                                boxShadow: details ? 'none' : '0px 2px 10px 0px rgba(68, 73, 103, 0.10)',
                                '&:focus-visible': { outline: '2px solid var(--lm-accent)', outlineOffset: 2 },
                            }}
                        >
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: details ? '15px' : '16.5px',
                                    fontWeight: 400,
                                    lineHeight: 1.65,
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'anywhere',
                                    display: onOpen ? '-webkit-box' : 'block',
                                    WebkitLineClamp: onOpen ? 5 : undefined,
                                    WebkitBoxOrient: onOpen ? 'vertical' : undefined,
                                    overflow: onOpen ? 'hidden' : undefined,
                                }}
                            >
                                {body.content}
                            </Typography>
                        </Box>
                    </Box>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                        gap={0.75}
                        px={0.5}
                        sx={{ color: 'text.disabled' }}
                    >
                        <Typography
                            variant="body2"
                            color="textDisabled"
                            noWrap
                            sx={{ fontWeight: 400 }}
                        >
                            {details ? dayjs(body.date).format('D MMMM YYYY') : messageAge}
                        </Typography>
                        <Box
                            component="span"
                            flexShrink={0}
                            sx={{
                                width: 3,
                                height: 3,
                                borderRadius: '50%',
                                bgcolor: '#C7D0DC',
                            }}
                        />
                        <Typography
                            variant="body2"
                            color="textDisabled"
                            noWrap
                            sx={{ fontWeight: 400 }}
                        >
                            {messageTime}
                        </Typography>
                    </Box>
                </Box>
            </>
        );
    };
    // Если сообщение удалено, не рендерим карточку
    if (isRemoved) {
        return null;
    }

    return (
        <LoadingWrapper
            isLoading={false}
            isLocal
            boxWrapperProps={details ? { sx: { height: '100%' } } : undefined}
        >
            {renderContextMenuCard()}
            <Card
                component="article"
                variant="outlined"
                onClick={
                    onOpen
                        ? (event) => {
                              if ((event.target as HTMLElement).closest('button, a, input, label, [role="button"]'))
                                  return;
                              if (window.getSelection()?.toString()) return;
                              const trigger = event.currentTarget.querySelector<HTMLElement>('[data-lead-open]');
                              if (trigger) onOpen(body.id, trigger);
                          }
                        : undefined
                }
                sx={{
                    ...getCardFeedbackSx(feedback),
                    ...(isSelected && { outline: '2px solid var(--lm-accent)', outlineOffset: -2 }),
                    ...(onOpen && { cursor: 'pointer', '&:hover': { borderColor: 'var(--lm-muted)' } }),
                    ...(details && {
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        border: 0,
                        borderRadius: 0,
                        boxShadow: 'none',
                        bgcolor: 'transparent',
                        opacity: 1,
                        '& .MuiCardContent-root': {
                            p: 0,
                            py: 2,
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            overscrollBehavior: 'contain',
                        },
                        '& .MuiCardActions-root': {
                            p: 0,
                            pt: 1.5,
                            gap: 2,
                            flexShrink: 0,
                            borderTop: '1px solid var(--lm-border)',
                        },
                    }),
                }}
            >
                {details && (
                    <Box sx={{ overflowWrap: 'anywhere', flexShrink: 0 }}>
                        <Typography sx={{ fontWeight: 600, mb: 1 }}>
                            {body.stream_display_name || body.stream_name || body.chat_name}
                        </Typography>
                        {body.chat_name && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {body.chat_name}
                            </Typography>
                        )}
                        {body.location && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Локация группы: {body.location}
                            </Typography>
                        )}
                        {body.link && (
                            <Button
                                component="a"
                                href={body.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                                startIcon={<ArrowUpRightInCircle />}
                                sx={{ mt: 1 }}
                            >
                                Открыть источник
                            </Button>
                        )}
                    </Box>
                )}
                <Box
                    display={details ? 'none' : 'flex'}
                    alignItems="flex-start"
                    gap={1}
                    px={1}
                    sx={{ mt: 1.5 }}
                >
                    {checkbox && (
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            minWidth={40}
                        >
                            {checkbox}
                        </Box>
                    )}
                    <Box
                        flex={1}
                        minWidth={0}
                    >
                        {renderTitle()}
                    </Box>
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="flex-end"
                        gap={0.25}
                        minWidth={140}
                        maxWidth="42%"
                        flexShrink={0}
                        overflow="hidden"
                    >
                        {renderSource()}
                    </Box>
                </Box>
                <CardContent
                    component={details ? 'section' : 'div'}
                    aria-label={details ? 'Текст лида' : undefined}
                    tabIndex={details ? 0 : undefined}
                    sx={{
                        width: '100%',
                        '&:focus-visible': { outline: '2px solid var(--lm-accent)', outlineOffset: -2 },
                    }}
                >
                    {renderMessage()}
                </CardContent>
                <CardActions
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 8,
                        flexWrap: 'wrap',
                    }}
                    disableSpacing
                >
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        gap={1}
                    >
                        <Button
                            variant="outlined"
                            size="medium"
                            color="secondary"
                            sx={{
                                fontSize: '14px',
                                border: '1px solid var(--lm-border)',
                                borderRadius: '8px',
                                fontWeight: 400,
                                textTransform: 'none',
                            }}
                            startIcon={<ArrowUpRightInCircle />}
                            onClick={() => {
                                window.open(body.author, '_blank');
                            }}
                        >
                            Написать
                        </Button>
                    </Box>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        sx={details ? { flexWrap: 'wrap' } : undefined}
                    >
                        {feedbackLabel && (
                            <Typography
                                variant="body2"
                                sx={{
                                    borderRadius: '999px',
                                    bgcolor: feedback === 'good' ? 'var(--lm-success-bg)' : 'var(--lm-canvas)',
                                    color: feedback === 'good' ? 'var(--lm-success-ink)' : 'var(--lm-muted)',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    lineHeight: '18px',
                                    mr: 0.5,
                                    px: 1,
                                    py: 0.25,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {feedbackLabel}
                            </Typography>
                        )}
                        <Tooltip title="Хороший лид">
                            <FeedbackActionButton
                                feedbackTone="good"
                                isActive={feedback === 'good'}
                                size="small"
                                aria-label="Хороший лид"
                                disabled={isFeedbackSaving}
                                onClick={() => handleFeedback('good')}
                            >
                                <Checked
                                    fillColor={feedback === 'good' ? 'var(--lm-success-ink)' : 'currentColor'}
                                    fontColor="var(--lm-surface)"
                                />
                            </FeedbackActionButton>
                        </Tooltip>
                        <Tooltip title="Не подходит">
                            <FeedbackActionButton
                                feedbackTone="bad"
                                isActive={feedback === 'bad'}
                                size="small"
                                aria-label="Не подходит"
                                disabled={isFeedbackSaving}
                                onClick={() => handleFeedback('bad')}
                            >
                                <BadFeedbackIcon>×</BadFeedbackIcon>
                            </FeedbackActionButton>
                        </Tooltip>
                        <IconButton
                            size="medium"
                            aria-label="Скопировать ссылку на лид"
                            onClick={() => {
                                copy(body.link);
                                enqueueSnackbar('Ссылка скопирована', { variant: 'success' });
                            }}
                            sx={{ padding: 0.5, color: 'text.secondary' }}
                        >
                            <Copy />
                        </IconButton>
                        {/* Удаление доступно только для сообщений из мониторинга */}
                        {body.source !== 'stream' && (
                            <IconButton
                                size="medium"
                                aria-label="Удалить лид"
                                onClick={handleRemove}
                                disabled={isLoadingRemove}
                                sx={{
                                    padding: 0.5,
                                    color: 'text.secondary',
                                    '&:hover': {
                                        color: 'rgba(228,63,63,0.8)',
                                    },
                                }}
                            >
                                <TrashIcon />
                            </IconButton>
                        )}
                        <IconButton
                            size="medium"
                            aria-label={body.favorite ? 'Убрать из избранного' : 'В избранное'}
                            aria-pressed={!!body.favorite}
                            onClick={handleFavoriteClick}
                            disabled={isLoading}
                            sx={{
                                padding: 0.5,
                            }}
                        >
                            <Bookmark fill={body.favorite} />
                        </IconButton>
                    </Box>
                </CardActions>
            </Card>
        </LoadingWrapper>
    );
};
